import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LogIn, User, Shield, UserCircle, LogOut } from 'lucide-react';
import AuthModal from './Auth/AuthModal';
import UserMenu from './Auth/UserMenu';
import BookingTracker from './Auth/BookingTracker';

const Header = ({ onMenuToggle }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    try {
      const userData = localStorage.getItem('user_data');
      if (userData && userData !== 'undefined' && userData !== 'null') {
        const parsed = JSON.parse(userData);
        if (parsed && parsed.email) {
          setUser(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      localStorage.removeItem('user_data');
      setUser(null);
    }

    // Listen for custom event to open auth modal
    const handleOpenAuthModal = (e) => {
      const mode = e.detail?.mode || 'login';
      setAuthModalMode(mode);
      setShowAuthModal(true);
    };

    window.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => window.removeEventListener('openAuthModal', handleOpenAuthModal);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  const toggleMenu = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    // Notify parent component if callback provided
    if (onMenuToggle) {
      onMenuToggle(newState);
    }
  };

  const navLinks = [
    { name: 'Book Now', path: '/book', highlight: true },
    { name: 'Home', path: '/' },
    { name: 'Services', path: '#services' },
    { name: 'Gallery', path: '#gallery' },
    { name: 'Engineers', path: '#engineers' },
    { name: 'Bundles', path: '#bundles' },
    { name: 'Artist Plan', path: '/artist-plan' }
  ];

  return (
    <>
    <header
      className="fixed left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-purple-900/30 transition-[top] duration-200"
      style={{ top: 'var(--banner-h, 0px)' }}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_63dac94a-0f2a-4ccd-9e98-d528f614ae89/artifacts/78xremtt_IMG_3108.jpeg"
              alt="High Quality Studios"
              className="h-12 w-12 rounded-full object-cover border-2 border-purple-500/50 transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navLinks.map((link) => (
              link.highlight ? (
                <a
                  key={link.name}
                  href={link.path}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full font-medium hover:from-purple-700 hover:to-blue-700 transition-all hover:scale-105"
                >
                  {link.name}
                </a>
              ) : (
                <a
                  key={link.name}
                  href={link.path}
                  className="text-gray-300 hover:text-white transition-colors relative group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-600 group-hover:w-full transition-all"></span>
                </a>
              )
            ))}
            
            {/* Auth Button/User Menu */}
            {user && user.email ? (
              <UserMenu user={user} onLogout={handleLogout} />
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setAuthModalMode('login'); setShowAuthModal(true); }}
                  className="flex items-center space-x-2 px-5 py-2 border-2 border-purple-500/50 text-white hover:border-purple-400 hover:bg-purple-600/20 rounded-full transition-all"
                >
                  <LogIn size={18} />
                  <span className="text-sm font-semibold">Login</span>
                </button>
                <button
                  onClick={() => { setAuthModalMode('signup'); setShowAuthModal(true); }}
                  className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-full transition-all hover:scale-105 shadow-lg shadow-purple-500/50"
                >
                  <User size={18} className="text-white" />
                  <span className="text-white font-semibold">Sign Up Free</span>
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button + Auth Buttons */}
          <div className="md:hidden flex items-center gap-1.5">
            {!isMenuOpen && (
              <>
                {!user?.email ? (
                  <>
                    <a
                      href="/book"
                      className="flex items-center px-2.5 py-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-full transition-all text-white text-xs font-semibold"
                    >
                      <span>Book</span>
                    </a>
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="flex items-center space-x-1 px-2.5 py-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-full transition-all text-white text-xs font-semibold"
                    >
                      <User size={12} />
                      <span>Sign Up</span>
                    </button>
                  </>
                ) : (
                  <a
                    href="/book"
                    className="flex items-center px-2.5 py-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-full transition-all text-white text-xs font-semibold"
                  >
                    <span>Book</span>
                  </a>
                )}
              </>
            )}
            <button
              onClick={toggleMenu}
              className="text-white p-2"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
    </header>

    {/* Mobile Navigation - Fixed overlay outside header */}
    {isMenuOpen && (
      <nav className="md:hidden fixed inset-0 top-[calc(80px+32px+env(safe-area-inset-top,0px))] bg-black/40 backdrop-blur-md z-[60] overflow-y-auto overscroll-contain">
        <div className="py-6 px-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                onClick={toggleMenu}
                className={`block py-3 px-4 rounded-lg transition-all duration-200 ${
                  link.highlight
                    ? 'text-purple-400 font-semibold bg-purple-600/20 border border-purple-500/30 mb-3'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </a>
            ))}
            
            {/* Mobile Auth/User Section */}
            <div className="pt-6 pb-20 border-t border-white/10 mt-6">
              {user && user.email ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="bg-purple-600/20 p-2 rounded-full">
                      <User className="text-purple-400" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{user.name}</p>
                      <p className="text-gray-400 text-sm truncate">{user.email}</p>
                    </div>
                  </div>
                  
                  {/* User Stats */}
                  <div className="mb-3 bg-gray-900/50 rounded-lg p-3 border border-purple-500/20">
                    <BookingTracker userEmail={user.email} compact={true} />
                  </div>
                  
                  {user.is_admin && (
                    <a
                      href="/admin"
                      onClick={toggleMenu}
                      className="flex items-center space-x-3 py-3 px-4 text-purple-300 hover:bg-purple-600/20 rounded-lg transition-colors"
                    >
                      <Shield size={18} />
                      <span>Admin Panel</span>
                    </a>
                  )}
                  {user.is_employee && user.engineer_id && (
                    <a
                      href="/employee"
                      onClick={toggleMenu}
                      className="flex items-center space-x-3 py-3 px-4 text-blue-300 hover:bg-blue-600/20 rounded-lg transition-colors"
                    >
                      <UserCircle size={18} />
                      <span>Employee Dashboard</span>
                    </a>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      toggleMenu();
                    }}
                    className="w-full flex items-center space-x-3 py-3 px-4 text-gray-300 hover:bg-purple-600/20 rounded-lg transition-colors"
                  >
                    <LogOut size={18} />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setAuthModalMode('signup');
                      setShowAuthModal(true);
                      toggleMenu();
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all"
                  >
                    <User size={18} />
                    <span>Sign Up Free</span>
                  </button>
                  <button
                    onClick={() => {
                      setAuthModalMode('login');
                      setShowAuthModal(true);
                      toggleMenu();
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-purple-500 text-white rounded-lg font-semibold hover:bg-purple-600/20 transition-all"
                  >
                    <LogIn size={18} />
                    <span>Login</span>
                  </button>
                </div>
              )}
            </div>
        </div>
      </nav>
    )}
    
    {/* Auth Modal - Rendered outside header for proper z-index layering */}
    {showAuthModal && (
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        defaultMode={authModalMode}
      />
    )}
    </>
  );
};

export default Header;